import { z } from 'zod';
import { newContactShareSchema } from './contactAccess.js';
/**
 * Contacts (v2.0, ADR-0006/0007) — the unified record for Clients / Referral
 * Sources / Staff. Shared client↔server so the SPA validates identically.
 */
export const CONTACT_CATEGORIES = ['client', 'referral_source', 'staff'];
export function isContactCategory(c) {
    return CONTACT_CATEGORIES.includes(c);
}
export const CONTACT_SHARE_ACCESS = ['view', 'edit'];
const uuid = z.string().uuid();
export const contactCreateSchema = z.object({
    category: z.enum(CONTACT_CATEGORIES).default('client'),
    firstName: z.string().min(1).max(120),
    lastName: z.string().min(1).max(120),
    email: z.string().email().max(200).optional(),
    phone: z.string().max(40).optional(),
    stageId: uuid.optional(),
    statusId: uuid.optional(),
    ownerUserId: uuid.optional(),
    contactTypeId: uuid.optional(),
    referralSourceId: uuid.optional(),
    providerId: uuid.optional(),
    programId: uuid.optional(),
    accruingLicensure: z.boolean().optional(),
});
/**
 * POST /contacts. ehr#848: `share` is Add Client's "Add for myself & share"; it needs the
 * "Share a new contact at creation" right (SHARE_AT_CREATE_RIGHT).
 */
export const contactCreateBodySchema = contactCreateSchema.extend({
    share: newContactShareSchema.optional(),
});
export const SHARE_AT_CREATE_RIGHT = 'share_contact_at_creation';
export const contactUpdateSchema = contactCreateSchema.partial().extend({
    // Board drag-to-move allows clearing the stage/status (→ Unassigned).
    stageId: uuid.nullable().optional(),
    statusId: uuid.nullable().optional(),
    // #112 - primary appointment location (a locations id); null clears it.
    primaryLocationId: uuid.nullable().optional(),
});
export const contactShareSchema = z.object({
    userId: uuid,
    access: z.enum(CONTACT_SHARE_ACCESS).default('view'),
});
export const contactReassignSchema = z.object({ ownerUserId: uuid });
/**
 * Bulk contact action (Batch 3 UI parity — mockup's bulk-action bar). One
 * operation applied to a selected set of ids. `value` is the target stage /
 * status / owner id for those actions; `archive` needs no value. Admin/manager
 * only (server-gated), matching single-item reassign/archive.
 */
export const CONTACT_BULK_ACTIONS = [
    'stage',
    'status',
    'reassign',
    'archive',
    'unarchive',
];
const VALUELESS_BULK_ACTIONS = ['archive', 'unarchive'];
export const contactBulkSchema = z
    .object({
    ids: z.array(uuid).min(1).max(200),
    action: z.enum(CONTACT_BULK_ACTIONS),
    value: uuid.optional(),
})
    .refine((b) => VALUELESS_BULK_ACTIONS.includes(b.action) || b.value !== undefined, {
    message: 'value is required for this action',
    path: ['value'],
});
/**
 * Demographics (Phase 13.1, V27 CDemo) — the client demographics
 * form (identity / contact / guardian / emergency / careteam / insurance /
 * guarantor / pharmacy / referral / employment). Stored as one encrypted JSON blob, so the
 * schema stays a lenient object; individual fields evolve with the form.
 */
export const DEMOGRAPHICS_SECTIONS = [
    'identity',
    'contact',
    'guardian',
    'emergency',
    'careteam',
    'insurance',
    'guarantor',
    'pharmacy',
    // ehr#743: Referral split out of Employment (Referral first).
    'referral',
    'employment',
];
export const demographicsSchema = z.record(z.string(), z.unknown());
/** Stages & statuses are org-scoped, Settings-managed name+position rows. */
export const pipelineItemSchema = z.object({
    name: z.string().min(1).max(120),
    position: z.number().int().min(0).optional(),
});
//# sourceMappingURL=contacts.js.map